import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { WorkItemUpdate } from 'src/Models/HistoryItem';
import { IActionsSummary } from 'src/manageAction/interfaces/ActionsSummary';
import { IRemoteWorkItemModelSummary } from 'src/manageAction/interfaces/RemoteWorkItemModelSummary';
import { IWorkItem } from 'src/manageAction/interfaces/WorkItem';
import type { WorkItem } from 'src/Models/WorkItem';
import { UATConstants } from '../../../Common/Models/UATConstants';
import {
  MANAGE_ACTION_API_CONSTANTS,
  MANAGE_ACTION_CONSTANTS,
} from '../../utils/manageActionConstants';
import {
  IActionOwnerForAreaEOU,
  IOpportunity,
  IPartnerInfo,
  IReferral,
  ISavePartnerOneInfo,
  ITpidResponse,
} from 'src/Common/RTK/interface/ViewFolder';
import { GPSTask } from 'src/GPSInvestments/Models/GPSTasks';
import { ActionOwnerMapping } from 'src/Models/ActionOwnerMapping';

export interface IacrRoiModel {
  opportunityId: string;
  acrCalcModel: any;
}

// User response from /api/users endpoint
export interface UserResponse {
  SubjectKind?: string;
  MetaType?: string;
  DirectoryAlias?: string;
  Domain?: string;
  PrincipalName?: string;
  MailAddress?: string;
  Origin?: string;
  OriginId?: string;
  DisplayName?: string;
  Url?: string;
  Descriptor?: string;
  _links?: {
    self?: { Href?: string };
    memberships?: { Href?: string };
    membershipState?: { Href?: string };
    storageKey?: { Href?: string };
    avatar?: { Href?: string };
  };
}

// Mapped user for grounding menu
export interface GroundingUser {
  id: string;
  primaryText: string;
  secondaryText: string;
  email: string;
  avatarSrc?: string;
}

export const getActionDataApi = createApi({
  reducerPath: 'getActionDataApi',
  baseQuery: fetchBaseQuery({
    baseUrl: UATConstants.WebApiUrl,
    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_API);
      const coreAPIToken = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_CORE_API);
      const graphToken = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_GRAPH);

      if (token && graphToken && coreAPIToken) {
        headers.set('Authorization', `Bearer ${token}`);
        headers.set('Coreapitoken', coreAPIToken);
        headers.set('Graphtoken', graphToken);
      }
      return headers;
    },
  }),
  tagTypes: ['getActionData', 'getComments', 'getTasks'], // Define the tags for this API
  endpoints: (builder) => ({
    getActionData: builder.query<IWorkItem, string>({
      query: (actionId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetAction}/${actionId}`,
      }),
      providesTags: ['getActionData'],
    }),
    getSearchTaskResults: builder.query<any[], string>({
      query: (searchTask) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.SearchRelatedActionItem}/?searchTask=${searchTask}`,
      }),
    }),
    getHistory: builder.query<WorkItemUpdate[], number>({
      query: (workItemId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetWorkItemHistory}/?workItemId=${workItemId}`,
      }),
    }),
    uploadAttachments: builder.mutation<any[], string[]>({
      query: (payload) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.uploadAttachments}`,
        body: payload,
      }),
    }),
    getRelatedWorkItems: builder.mutation<IActionsSummary, string[]>({
      query: (taskIds) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetRelatedWorkItems}`,
        body: taskIds,
      }),
    }),
    postSearchTaskResults: builder.mutation<any[], string>({
      query: (searchTask) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.SearchRelatedActionItem}/?searchTask=${searchTask}`,
        body: searchTask,
      }),
    }),
    postSearchRelatedItemsWithAreaPath: builder.mutation<any[], { linkType: string; searchTask: string; areaPaths: string[] }>({
      query: ({ linkType, searchTask, areaPaths }) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.SearchRelatedActionItemWithAreaPath}?linkType=${encodeURIComponent(linkType)}&searchTask=${encodeURIComponent(searchTask)}`,
        body: areaPaths,
      }),
    }),
    updateActionItem: builder.mutation<IWorkItem, string>({
      query: (actionObject) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.UpdateAction}`,
        body: actionObject,
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['getComments','getActionData']),
    }),
    updateDWRRequest: builder.mutation<IWorkItem, string>({
      query: (actionObject) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.UpdateDWRRequest}`,
        body: actionObject,
      }),
      invalidatesTags: ['getComments', 'getActionData'],
    }),
    getFollowing: builder.query<string, { workItemID: string; userid: string }>({
      query: (getFollowingData) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetFollowing}?workItemID=${getFollowingData?.workItemID}&userID=${getFollowingData?.userid}`,
      }),
    }),
    addFollowing: builder.query<string, { workItemID: string; userid: string }>({
      query: (addFollowing) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.AddFollowing}?workItemID=${addFollowing?.workItemID}&userID=${addFollowing?.userid}`,
      }),
    }),
    deleteFollowing: builder.query<string, { workItemID: string; userid: string }>({
      query: (deleteFollowing) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.DeleteFollowing}?workItemID=${deleteFollowing?.workItemID}&userID=${deleteFollowing?.userid}`,
      }),
    }),
    getRelatedFeedbackItems: builder.query<any, number>({
      query: (actionId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetRelatedFeedbackItems}/?actionId=${actionId}`,
      }),
    }),
    postSearchRemoteWorkItemsList: builder.mutation<any[], string[]>({
      query: (remoteUrls) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.SearchRemoteWorkItemsList}`,
        body: remoteUrls,
      }),
    }),
    checkRemoteUrl: builder.mutation<IRemoteWorkItemModelSummary, string>({
      query: (remoteUrl) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.SearchRemoteWorkItem}?url=${remoteUrl}`,
      }),
    }),
    // getADOTags: builder.query<any, string>({
    //   query: () => ({
    //     method: 'GET',
    //     url: `${MANAGE_ACTION_API_CONSTANTS.GetADOTags}`,
    //   }),
    // }),
    getWorkItemComments: builder.query<any[], number>({
      query: (actionId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetWorkItemComments}/?workItemId=${actionId}`,
      }),
      providesTags: ['getComments'],
    }),
    PostComment: builder.mutation<any[], { workItem: WorkItem; content: string }>({
      query: (commentInfo) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.PostWorkItemComment}`,
        body: commentInfo,
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['getComments']),
    }),
    UpdateComment: builder.mutation<any, any>({
      query: (commentInfo) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.UpdateWorkItemComment}`,
        body: commentInfo,
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['getComments']),
    }),
    DeleteComment: builder.mutation<any[], { workItemId: string; commentId: string }>({
      query: (onDeleteCommentComplete) => ({
        method: 'DELETE',
        url: `${MANAGE_ACTION_API_CONSTANTS.DeleteWorkItemComment}/?workItemId=${onDeleteCommentComplete.workItemId}&commentId=${onDeleteCommentComplete.commentId}`,
        body: onDeleteCommentComplete,
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['getComments']),
    }),
    GetEmail: builder.query<any[], number>({
      query: (actionId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.getEmail}/?actionId=${actionId}`,
      }),
    }),
    GetGroupMembers: builder.query<any[], number>({
      query: (groupId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetGroupMembers}?groupId=${groupId}`,
      }),
    }),
    GetPartnerRegionAreaFromCountry: builder.query<object, object>({
      query: () => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.PartnerRegionAreaFromCountry}`,
      }),
    }),
    GetPartnerEngagementByReferralID: builder.query<IReferral[], string>({
      query: (referralId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetPartnerEngagementByReferralID}${referralId}`,
      }),
    }),
    GetCustomerByTPID: builder.query<ITpidResponse, string>({
      query: (tpid) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetCustomerByTPID}${tpid}`,
      }),
    }),
    GetByOpportunity: builder.query<IOpportunity, string>({
      query: (opportunityId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetByOpportunity}${opportunityId}`,
      }),
    }),
    GetWorkScopesList: builder.query<IOpportunity, string>({
      query: (opportunityId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetWorkScopes}${opportunityId}`,
      }),
    }),
    GetWorkscopeItemDetails: builder.query<IOpportunity, string>({
      query: (workScopeId: string) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetWorkItemDetails}${workScopeId}`,
      }),
    }),
    GetByOpportunityWithEcifAcrRoiCalculations: builder.query<IacrRoiModel, any>({
      query: (data: IacrRoiModel) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetByOpportunityWithEcifAcrRoiCalculations}${data?.opportunityId}`,
        body: data?.acrCalcModel,
      }),
    }),
    GetGroupMembersWithAreaEou: builder.query<
      any[],
      { areaText: string; eouText: string; encodedPrgname: string; encodedseg: string }
    >({
      query: (paramObj) => {
        const area = encodeURIComponent(paramObj.areaText || '');
        const eou = encodeURIComponent(paramObj.eouText || '');
        const programname = encodeURIComponent(paramObj.encodedPrgname || '');
        const segment = encodeURIComponent(paramObj.encodedseg || '');
        return {
          method: 'GET',
          url: `${UATConstants.ApiPaths.GetGroupMembersWithAreaEou}?area=${area}&eou=${eou}&programname=${programname}&segment=${segment}&`,
        };
      },
    }),
    GetGroupMembersforMaaS: builder.query<any, void>({
      query: () => ({
        method: 'GET',
        url: `${UATConstants.ApiPaths.GetGroupMembersforMaaS}`,
      }),
    }),
    GetGroupMembersforAHPM: builder.query<any, void>({
      query: () => ({
        method: 'GET',
        url: `${UATConstants.ApiPaths.GetGroupMembers}`,
      }),
    }),
    GetPartnerOneInfo: builder.query<ISavePartnerOneInfo[], string>({
      query: (partnerOneId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetPartnerOneInfo}${partnerOneId}`,
      }),
    }),
    getTasksInformation: builder.query<GPSTask[], number>({
      query: (actionId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetTaskInfo}?ActionId=${actionId}`,
      }),
      providesTags: ['getTasks'],
    }),
    saveTasksInformation: builder.mutation<unknown, GPSTask[]>({
      query: (tasksList) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.SaveTaskList}`,
        body: tasksList,
      }),
      invalidatesTags: ['getTasks'],
    }),
    GetByOpportunityWithMilestones: builder.query<IOpportunity, string>({
      query: (opportunityId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetByOpportunityWithMilestones}${opportunityId}`,
      }),
    }),
    GetMileStoneDetails: builder.query<Any, string>({
      query: (milestoneId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetMileStoneDetails}${milestoneId}`,
      }),
    }),
    GetAMMMilestoneSnapshot: builder.query<IOpportunity, string>({
      query: (actionId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetAMMMilestoneSnapshot}${actionId}`,
      }),
    }),
    getDeDupeData: builder.query<unknown, unknown>({
      query: (payload) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetDeDupOpportunities}`,
        body: payload,
      }),
    }),
    getActionOwnerForAreaEOU: builder.query<ActionOwnerMapping[], IActionOwnerForAreaEOU>({
      query: (payload) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetActionOwnerForAreaEOU}?area=${payload.area}&eou=${payload.eou}&programname=${payload.programname}&segment=${payload.segment}`,
      }),
    }),
    GetGroupMembersforAMM: builder.query<any, void>({
      query: () => ({
        method: 'GET',
        url: `${UATConstants.ApiPaths.GetGroupMembersforAMM}`,
      }),
    }),
    // POST /api/users - Search users by display name for grounding menu
    getUsers: builder.query<GroundingUser[], { displayName?: string }>({
      query: ({ displayName = '' }) => ({
        method: 'POST',
        url: '/api/users',
        body: {
          DisplayName: displayName,
        },
      }),
      transformResponse: (response: UserResponse[] | { value?: UserResponse[] }) => {
        console.log('Users API raw response:', response);

        // Handle both array response and object with value property
        let users: UserResponse[] = [];
        if (Array.isArray(response)) {
          users = response;
        } else if (response && Array.isArray(response.value)) {
          users = response.value;
        }

        if (users.length === 0) {
          console.log('No users found in response');
          return [];
        }

        return users.map((user) => ({
          id: user.OriginId || user.Descriptor || '',
          primaryText: user.DisplayName || '',
          secondaryText: user.MailAddress || user.PrincipalName || '',
          email: user.MailAddress || user.PrincipalName || '',
          avatarSrc: user._links?.avatar?.Href,
        }));
      },
    }),
    GetGroupMembersForMDC: builder.query<any, void>({
      query: () => ({
        method: 'GET',
        url: `${UATConstants.ApiPaths.GetGroupMembersForMDC}`,
      }),
    }),
    ValidateSRNumbers: builder.mutation<
      {
        total: number;
        validCount: number;
        invalidCount: number;
        results: Array<{ srNumber: string; isValid: boolean; message: string }>;
      },
      { srNumbers: string[] }
    >({
      query: (payload) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.ValidateSRNumbers}`,
        body: payload,
      }),
    }),
  }),
});

export const {
  useGetActionDataQuery,
  useGetRelatedWorkItemsMutation,
  useUploadAttachmentsMutation,
  useGetSearchTaskResultsQuery,
  usePostSearchTaskResultsMutation,
  usePostSearchRelatedItemsWithAreaPathMutation,
  useUpdateActionItemMutation,
  useUpdateDWRRequestMutation,
  useGetHistoryQuery,
  useGetFollowingQuery,
  useAddFollowingQuery,
  useDeleteFollowingQuery,
  useGetRelatedFeedbackItemsQuery,
  usePostSearchRemoteWorkItemsListMutation,
  useCheckRemoteUrlMutation,
  useGetWorkItemCommentsQuery,
  useGetADOTagsQuery,
  usePostCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useGetEmailQuery,
  useGetPartnerRegionAreaFromCountryQuery,
  useGetTasksInformationQuery,
  useSaveTasksInformationMutation,
  useGetByOpportunityWithMilestonesQuery,
  useGetMileStoneDetailsQuery,
  useGetAMMMilestoneSnapshotQuery,
  getDeDupeData,
  useGetActionOwnerForAreaEOUQuery,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetGroupMembersForMDCQuery,
  useValidateSRNumbersMutation,
} = getActionDataApi;
// Generated by GitHub Copilot | Updated on: 2026-04-11 | 8 lines modified | Added searchTechROBActions mutation calling GetDashboardActions with areaPaths for TechROB-scoped search

// Generated by GitHub Copilot | Updated on: 2026-04-13 | 8 lines added | Add postSearchRelatedItemsWithAreaPath mutation for TechROB-scoped ADO search

// Generated by GitHub Copilot | Updated on: 2026-06-19 | 12 lines added | Added ValidateSRNumbers mutation endpoint for SR number API validation
// Generated by GitHub Copilot | Updated on: 2026-06-17 | 12 lines modified | Added AONAcrAndRoiDetails RTK query endpoint with POST method and IAONAcrAndRoiDetailsPayload interface
